import { useState, type ReactElement } from 'react';

/* ═══════════════════════════════════════════════════════════════════════════
   LE MENU DÉVELOPPEUR.

   Outil de TEST, pas de jeu. Il s'ouvre depuis la pause (uniquement sous
   `import.meta.env.DEV`) et permet de : se téléporter à n'importe quel niveau ou
   combat de boss, régler le loadout dans le détail, s'accorder des améliorations
   en direct, et tricher sur la méta pour éprouver les écrans hors-jeu.

   Rien ici ne respecte l'économie du jeu — c'est le but. La logique vit dans
   `main.ts` (les fonctions `dev*`) ; cet écran n'est qu'une façade qui l'appelle.
   ═══════════════════════════════════════════════════════════════════════════ */

interface Named {
  readonly id: string;
  readonly name: string;
}
interface WorldRow {
  readonly id: string;
  readonly name: string;
  readonly levels: readonly string[];
}
interface ModRow {
  readonly id: string;
  readonly name: string;
  readonly text: string;
}

export interface DevScreenProps {
  readonly worlds: readonly WorldRow[];
  readonly ships: readonly Named[];
  readonly weapons: readonly Named[];
  readonly secondaries: readonly Named[];
  readonly bombs: readonly Named[];
  readonly mods: readonly ModRow[];
  readonly difficultyNames: readonly string[];
  readonly current: {
    readonly shipId: string;
    readonly weaponId: string;
    readonly secondaryId: string;
    readonly bombId: string;
    readonly difficulty: number;
    readonly level: number;
    readonly credits: number;
  };
  readonly specs: {
    readonly damage: number;
    readonly fireRate: number;
    readonly bulletCount: number;
    readonly bulletSpeed: number;
    readonly dps: number;
    readonly secondaryCooldownMul: number;
    readonly secondaryDamageMul: number;
  };
  readonly onSpec: (kind: string) => void;
  readonly onResetSpecs: () => void;
  readonly preLevels: number;
  readonly onPreLevels: (n: number) => void;
  readonly onJump: (worldIndex: number, levelIndex: number) => void;
  readonly onJumpBoss: (worldIndex: number) => void;
  readonly onEndless: () => void;
  readonly onReplay: () => void;
  readonly onSetLoadout: (patch: {
    shipId?: string;
    weaponId?: string;
    secondaryId?: string;
    bombId?: string;
    lastDifficulty?: number;
  }) => void;
  readonly onGrantLevels: (n: number) => void;
  readonly onGrantMod: (modId: string) => void;
  readonly onRespec: () => void;
  readonly onCheat: (kind: 'credits' | 'skill' | 'all') => void;
  readonly onResume: () => void;
  readonly onMenu: () => void;
}

const PRE_LEVELS = [0, 5, 10, 20] as const;

export function DevScreen(p: DevScreenProps): ReactElement {
  const [filter, setFilter] = useState('');
  const mods = filter
    ? p.mods.filter((m) => (m.name + ' ' + m.text).toLowerCase().includes(filter.toLowerCase()))
    : p.mods;

  const sel = (
    value: string,
    options: readonly Named[],
    onChange: (v: string) => void,
  ): ReactElement => (
    <select className="dev-select" value={value} onChange={(e) => onChange(e.target.value)}>
      {options.map((o) => (
        <option key={o.id} value={o.id}>
          {o.name}
        </option>
      ))}
    </select>
  );

  return (
    <div className="screen dev">
      <h2>MENU DÉVELOPPEUR</h2>
      <p className="dev-sub">
        Niveau courant {p.current.level} · {p.current.credits.toLocaleString('fr-FR')} crédits — outils
        de test, hors économie du jeu.
      </p>

      <div className="dev-cols">
        {/* ── MISSION : téléportation ─────────────────────────────────────── */}
        <section className="dev-sec">
          <h3>MISSION</h3>
          <div className="dev-pre">
            <span>Pré-niveaux au lancement :</span>
            {PRE_LEVELS.map((n) => (
              <button
                key={n}
                className={`dev-chip ${p.preLevels === n ? 'on' : ''}`}
                onClick={() => p.onPreLevels(n)}
              >
                {n === 0 ? 'aucun' : `+${n}`}
              </button>
            ))}
          </div>
          {p.worlds.map((w, wi) => (
            <div key={w.id} className="dev-world">
              <span className="dev-world-name">
                Monde {wi + 1} · {w.name}
              </span>
              <div className="dev-row">
                {w.levels.map((_lvl, li) => (
                  <button key={li} className="dev-chip" onClick={() => p.onJump(wi, li)}>
                    N{li + 1}
                  </button>
                ))}
                <button className="dev-chip boss" onClick={() => p.onJumpBoss(wi)}>
                  ▶ boss
                </button>
              </div>
            </div>
          ))}
          <div className="dev-row">
            <button className="dev-chip" onClick={p.onEndless}>
              ∞ SANS FIN
            </button>
            <button className="dev-chip" onClick={p.onReplay}>
              ⟲ rejouer ici
            </button>
          </div>
        </section>

        {/* ── LOADOUT ─────────────────────────────────────────────────────── */}
        <section className="dev-sec">
          <h3>LOADOUT</h3>
          <p className="dev-hint">
            Appliqué EN DIRECT (même en pleine partie). Le contenu verrouillé est
            jouable ici sans le débloquer.
          </p>
          <label className="dev-field">
            <span>Vaisseau</span>
            {sel(p.current.shipId, p.ships, (v) => p.onSetLoadout({ shipId: v }))}
          </label>
          <label className="dev-field">
            <span>Arme principale</span>
            {sel(p.current.weaponId, p.weapons, (v) => p.onSetLoadout({ weaponId: v }))}
          </label>
          <label className="dev-field">
            <span>Arme secondaire</span>
            {sel(p.current.secondaryId, p.secondaries, (v) => p.onSetLoadout({ secondaryId: v }))}
          </label>
          <label className="dev-field">
            <span>Bombe</span>
            {sel(p.current.bombId, p.bombs, (v) => p.onSetLoadout({ bombId: v }))}
          </label>
          <label className="dev-field">
            <span>Difficulté</span>
            <select
              className="dev-select"
              value={p.current.difficulty}
              onChange={(e) => p.onSetLoadout({ lastDifficulty: Number(e.target.value) })}
            >
              {p.difficultyNames.map((n, i) => (
                <option key={n} value={i}>
                  {n}
                </option>
              ))}
            </select>
          </label>
        </section>

        {/* ── SPECS D'ARME (améliore les caractéristiques en direct) ──────── */}
        <section className="dev-sec">
          <h3>SPECS DU VAISSEAU</h3>
          <p className="dev-hint">
            Améliore les caractéristiques de l’arme principale/secondaire, en
            direct et cumulable.
          </p>
          <div className="dev-specs">
            <div className="dev-spec">
              <span>Dégâts</span>
              <b>{p.specs.damage.toFixed(0)}</b>
              <button className="dev-chip" onClick={() => p.onSpec('damage')}>
                +25%
              </button>
            </div>
            <div className="dev-spec">
              <span>Cadence</span>
              <b>{p.specs.fireRate.toFixed(2)}/s</b>
              <button className="dev-chip" onClick={() => p.onSpec('fireRate')}>
                +25%
              </button>
            </div>
            <div className="dev-spec">
              <span>Projectiles</span>
              <b>{p.specs.bulletCount}</b>
              <button className="dev-chip" onClick={() => p.onSpec('bulletCount')}>
                +1
              </button>
            </div>
            <div className="dev-spec">
              <span>Vitesse balle</span>
              <b>{p.specs.bulletSpeed.toFixed(0)}</b>
              <button className="dev-chip" onClick={() => p.onSpec('bulletSpeed')}>
                +20%
              </button>
            </div>
            <div className="dev-spec">
              <span>Recharge sec.</span>
              <b>×{p.specs.secondaryCooldownMul.toFixed(2)}</b>
              <button className="dev-chip" onClick={() => p.onSpec('secCooldown')}>
                −25%
              </button>
            </div>
            <div className="dev-spec">
              <span>Dégâts sec.</span>
              <b>×{p.specs.secondaryDamageMul.toFixed(2)}</b>
              <button className="dev-chip" onClick={() => p.onSpec('secDamage')}>
                +25%
              </button>
            </div>
            <div className="dev-spec">
              <span>DPS estimé</span>
              <b>{p.specs.dps.toFixed(0)}</b>
            </div>
          </div>
          <button className="dev-chip warn" onClick={p.onResetSpecs}>
            réinitialiser les specs
          </button>
        </section>

        {/* ── AMÉLIORATIONS (en direct sur la run) ────────────────────────── */}
        <section className="dev-sec wide">
          <h3>AMÉLIORATIONS (live)</h3>
          <div className="dev-row">
            <button className="dev-chip" onClick={() => p.onGrantLevels(1)}>
              niveau +1
            </button>
            <button className="dev-chip" onClick={() => p.onGrantLevels(5)}>
              +5
            </button>
            <button className="dev-chip" onClick={() => p.onGrantLevels(20)}>
              build max
            </button>
            <button className="dev-chip warn" onClick={p.onRespec}>
              vider
            </button>
          </div>
          <input
            className="dev-search"
            placeholder="filtrer les améliorations…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <div className="dev-mods">
            {mods.map((m) => (
              <button key={m.id} className="dev-mod" onClick={() => p.onGrantMod(m.id)}>
                <span className="dev-mod-name">+ {m.name}</span>
                <span className="dev-mod-text">{m.text}</span>
              </button>
            ))}
          </div>
        </section>

        {/* ── TRICHE MÉTA ─────────────────────────────────────────────────── */}
        <section className="dev-sec">
          <h3>TRICHE MÉTA</h3>
          <div className="dev-row wrap">
            <button className="dev-chip" onClick={() => p.onCheat('credits')}>
              +5000 crédits
            </button>
            <button className="dev-chip" onClick={() => p.onCheat('skill')}>
              tous les jalons
            </button>
            <button className="dev-chip" onClick={() => p.onCheat('all')}>
              tout débloquer
            </button>
          </div>
        </section>
      </div>

      <div className="dev-foot">
        <button className="btn btn-primary" onClick={p.onResume}>
          REPRENDRE
        </button>
        <button className="btn" onClick={p.onMenu}>
          MENU
        </button>
      </div>
    </div>
  );
}
